/**
 * File: taxCalendar.ts
 * Path: packages/config/src/accounting/taxCalendar.ts
 * Description: Every tax and filing date that applies to a set of books,
 * worked out from its financial year: the three provisional tax payments
 * (IRP6), the income tax return (ITR14 / ITR12), and, when switched on,
 * monthly PAYE/UIF/SDL (EMP201), the staff reconciliations (EMP501) and VAT
 * returns (VAT201). Pure: the server schedules push reminders from it and
 * the Tax page lists it, so the two can't disagree.
 *
 * Dates follow SARS (provisional tax page, read 2026-10-08): first payment
 * within six months of the year starting, second on the last business day of
 * the year, optional third on the last business day of September for a
 * February year end. A date that is a weekend or public holiday moves back
 * to the business day before it (always the safe side). SARS announces the
 * individual filing deadline each year, so that one can be overridden.
 */

import type { BooksProfile, ProfileKind } from "./accountingTypes";
import { financialYearEnd, financialYearStart } from "./ledger";
import type { TaxYearInputs } from "./taxYear";

/* ---------------------------------------------------------------------------
 * Business days
 * ------------------------------------------------------------------------ */

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
const utc = (day: string) => new Date(`${day}T00:00:00Z`);
const fromUtc = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (day: string, n: number) => fromUtc(new Date(utc(day).getTime() + n * 86_400_000));

/** Easter Sunday (the Gregorian computus). */
const easter = (year: number): string => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return iso(year, month, day);
};

const holidayCache = new Map<number, Set<string>>();

/** South Africa's public holidays in a year; a Sunday holiday also closes the Monday. */
export const publicHolidays = (year: number): Set<string> => {
  const cached = holidayCache.get(year);
  if (cached) return cached;
  const fixed = [iso(year, 1, 1), iso(year, 3, 21), iso(year, 4, 27), iso(year, 5, 1), iso(year, 6, 16), iso(year, 8, 9), iso(year, 9, 24), iso(year, 12, 16), iso(year, 12, 25), iso(year, 12, 26)];
  const days = new Set<string>();
  for (const day of fixed) {
    days.add(day);
    if (utc(day).getUTCDay() === 0) days.add(addDays(day, 1));
  }
  const easterDay = easter(year);
  days.add(addDays(easterDay, -2)); // Good Friday
  days.add(addDays(easterDay, 1)); // Family Day
  holidayCache.set(year, days);
  return days;
};

export const isBusinessDay = (day: string): boolean => {
  const weekday = utc(day).getUTCDay();
  return weekday !== 0 && weekday !== 6 && !publicHolidays(Number(day.slice(0, 4))).has(day);
};

/** `day` itself if it's a business day, otherwise the last business day before it. */
export const lastBusinessDayOnOrBefore = (day: string): string => {
  let d = day;
  while (!isBusinessDay(d)) d = addDays(d, -1);
  return d;
};

/** The `n`th business day after `day` (not counting `day` itself). */
export const addBusinessDays = (day: string, n: number): string => {
  let d = day;
  for (let left = n; left > 0; ) {
    d = addDays(d, 1);
    if (isBusinessDay(d)) left -= 1;
  }
  return d;
};

/* ---------------------------------------------------------------------------
 * CIPC annual return
 * ------------------------------------------------------------------------ */

/** CIPC: the annual return is due within this many business days of the incorporation anniversary. */
export const ANNUAL_RETURN_BUSINESS_DAYS = 30;

/** The anniversary of incorporation in `year` (29 February falls on the 28th) and the last day to file without the late fee. */
export const annualReturnDue = (incorporatedOn: string, year: number): { anniversary: string; dueOn: string } => {
  const month = Number(incorporatedOn.slice(5, 7));
  const dayOfMonth = Math.min(Number(incorporatedOn.slice(8, 10)), new Date(Date.UTC(year, month, 0)).getUTCDate());
  const anniversary = iso(year, month, dayOfMonth);
  return { anniversary, dueOn: addBusinessDays(anniversary, ANNUAL_RETURN_BUSINESS_DAYS) };
};

const monthEnd = (year: number, month: number) => iso(year, month, new Date(Date.UTC(year, month, 0)).getUTCDate());

/** The last day of the month `months` after the month `day` is in. */
const endOfMonthAfter = (day: string, months: number): string => {
  const total = Number(day.slice(0, 4)) * 12 + (Number(day.slice(5, 7)) - 1) + months;
  return monthEnd(Math.floor(total / 12), (total % 12) + 1);
};

/* ---------------------------------------------------------------------------
 * Obligations
 * ------------------------------------------------------------------------ */

export type ObligationKind =
  | "irp6-1"
  | "irp6-2"
  | "irp6-3"
  | "itr"
  | "emp201"
  | "emp501-interim"
  | "emp501-annual"
  | "vat201"
  | "cipc-ar";

export interface FilingRecord {
  id: string;
  /** The day it was filed or paid; null while it's still to do. */
  filedOn: string | null;
  /** A due date SARS announced that differs from the one worked out. */
  dueOnOverride: string | null;
  note: string;
}

export const FILINGS_COLLECTION = "filings";

/** Reads a stored filing, ignoring anything malformed. */
export const toFiling = (id: string, data: Record<string, unknown> | undefined | null): FilingRecord => {
  const r = data ?? {};
  const day = (v: unknown) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null);
  return { id, filedOn: day(r.filedOn), dueOnOverride: day(r.dueOnOverride), note: typeof r.note === "string" ? r.note : "" };
};

export const emptyFiling = (id: string): FilingRecord => ({ id, filedOn: null, dueOnOverride: null, note: "" });

export interface Obligation {
  id: string;
  kind: ObligationKind;
  title: string;
  /** What it is, for someone who has never done it. */
  plain: string;
  /** The date to meet (an override, if one was saved). */
  dueOn: string;
  /** The date worked out from the rules. */
  ruleDueOn: string;
  /** The financial year it belongs to (its last day). */
  yearEnd: string;
  /** One line on what to do, for the reminder. */
  action: string;
  /** Optional ones are worth doing but SARS doesn't insist (the third provisional payment). */
  optional: boolean;
  /** False when it depends on something switched off (staff, VAT). */
  applies: boolean;
  filedOn: string | null;
  note: string;
}

export interface CalendarInput {
  profile: Pick<BooksProfile, "kind" | "yearEndMonth" | "booksStart">;
  today: string;
  /** Saved tax-year settings, by the year's last day; missing years use the defaults. */
  years: Readonly<Record<string, Pick<TaxYearInputs, "hasEmployees" | "vatRegistered" | "vatCategory">>>;
  filings: Readonly<Record<string, FilingRecord>>;
  /** Company only: the date CIPC registered it; without it there's no annual return date. */
  incorporatedOn?: string | null;
}

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const monthYear = (day: string) => `${MONTHS[Number(day.slice(5, 7)) - 1]} ${day.slice(0, 4)}`;

/** VAT periods end on these months: A (odd), B (even), C (every month). */
const vatPeriodMonths = (category: TaxYearInputs["vatCategory"]): number[] =>
  category === "A" ? [1, 3, 5, 7, 9, 11] : category === "C" ? [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] : [2, 4, 6, 8, 10, 12];

export const obligationReminderDays = (kind: ObligationKind): number[] =>
  kind === "emp201" || kind === "vat201" ? [3, 1, 0] : kind === "cipc-ar" ? [30, 14, 7, 1, 0] : [14, 7, 1, 0];

const ACTIONS: Record<ObligationKind, string> = {
  "irp6-1": "Work out the amount on the Tax page, file the IRP6 on eFiling (even at R0) and pay, then tick it off.",
  "irp6-2": "Work out the amount on the Tax page, file the IRP6 on eFiling (even at R0) and pay, then tick it off.",
  "irp6-3": "Check on the Tax page whether a top-up is needed, then tick it off.",
  "itr": "Type the return into eFiling from the Tax page worksheet, then tick it off.",
  "emp201": "File the EMP201 on eFiling and pay, then tick it off.",
  "emp501-interim": "File the EMP501 on eFiling, then tick it off.",
  "emp501-annual": "File the EMP501 on eFiling, then tick it off.",
  "vat201": "File the VAT201 on eFiling and pay, then tick it off.",
  "cipc-ar": "Check the beneficial ownership declaration, then file the annual return on CIPC and tick it off.",
};

export const taxObligations = (input: CalendarInput): Obligation[] => {
  const { profile, today } = input;
  const personal = profile.kind === ("personal" satisfies ProfileKind);
  const firstEnd = financialYearEnd(profile.booksStart, profile.yearEndMonth);
  const lastEnd = financialYearEnd(today, profile.yearEndMonth);
  const out: Obligation[] = [];

  const add = (
    base: Omit<Obligation, "dueOn" | "filedOn" | "note" | "ruleDueOn" | "action"> & { ruleDueOn: string },
  ) => {
    const filing = input.filings[base.id];
    out.push({
      ...base,
      action: ACTIONS[base.kind],
      dueOn: filing?.dueOnOverride ?? base.ruleDueOn,
      filedOn: filing?.filedOn ?? null,
      note: filing?.note ?? "",
    });
  };

  for (let end = firstEnd, guard = 0; end <= lastEnd && guard < 40; guard += 1) {
    const start = financialYearStart(end, profile.yearEndMonth);
    const year = input.years[end];
    const hasEmployees = year?.hasEmployees === true;
    const vatRegistered = year?.vatRegistered === true;
    const label = `year ending ${end}`;

    add({
      id: `irp6-1-${end}`,
      kind: "irp6-1",
      title: "First provisional tax return (IRP6)",
      plain: `Tax is paid in instalments during the year instead of in one lump sum. This is the first instalment, for the ${label}: about half of what you expect to owe for the year.`,
      ruleDueOn: lastBusinessDayOnOrBefore(endOfMonthAfter(start, 5)),
      yearEnd: end,
      optional: false,
      applies: true,
    });
    add({
      id: `irp6-2-${end}`,
      kind: "irp6-2",
      title: "Second provisional tax return (IRP6)",
      plain: `The second instalment, for the ${label}, paid on the last working day of the year: the rest of what you expect to owe.`,
      ruleDueOn: lastBusinessDayOnOrBefore(end),
      yearEnd: end,
      optional: false,
      applies: true,
    });
    add({
      id: `irp6-3-${end}`,
      kind: "irp6-3",
      title: "Top-up provisional tax return (IRP6)",
      plain: `Optional. If the year turned out to earn more than you estimated, this tops up the tax before SARS charges interest. Only needed if the books now show more tax than you've paid for the ${label}.`,
      ruleDueOn: lastBusinessDayOnOrBefore(Number(end.slice(5, 7)) === 2 ? monthEnd(Number(end.slice(0, 4)), 9) : endOfMonthAfter(end, 6)),
      yearEnd: end,
      optional: true,
      applies: true,
    });
    add({
      id: `itr-${end}`,
      kind: "itr",
      title: personal ? "Your income tax return (ITR12)" : "The company's income tax return (ITR14)",
      plain: personal
        ? `Your tax return for the ${label}, typed into SARS eFiling. SARS announces the exact closing date each year (usually in January for people who pay provisional tax), so this date is early on purpose: change it on this page when SARS announces the real one.`
        : `The company's tax return for the ${label}, typed into SARS eFiling. It's due within 12 months of the year ending.`,
      ruleDueOn: lastBusinessDayOnOrBefore(personal ? iso(Number(end.slice(0, 4)) + 1, 1, 15) : endOfMonthAfter(end, 12)),
      yearEnd: end,
      optional: false,
      applies: true,
    });

    // EMP501: the reconciliation of what staff were paid and what was paid to SARS.
    add({
      id: `emp501-interim-${end}`,
      kind: "emp501-interim",
      title: "Staff tax reconciliation: half-year (EMP501)",
      plain: `Twice a year SARS wants a summary of what staff were paid and the tax taken off. This is the first half of the ${label}.`,
      ruleDueOn: lastBusinessDayOnOrBefore(endOfMonthAfter(start, 7)),
      yearEnd: end,
      optional: false,
      applies: hasEmployees,
    });
    add({
      id: `emp501-annual-${end}`,
      kind: "emp501-annual",
      title: "Staff tax reconciliation: full year (EMP501)",
      plain: `The full-year summary of staff pay and tax for the ${label}, with each person's IRP5 certificate.`,
      ruleDueOn: lastBusinessDayOnOrBefore(endOfMonthAfter(end, 3)),
      yearEnd: end,
      optional: false,
      applies: hasEmployees,
    });

    // EMP201: every month, the tax taken off pay, UIF and SDL.
    for (let m = 0; m < 12; m += 1) {
      const period = endOfMonthAfter(start, m);
      const next = endOfMonthAfter(period, 1);
      add({
        id: `emp201-${period.slice(0, 7)}`,
        kind: "emp201",
        title: `Staff tax for ${monthYear(period)} (EMP201)`,
        plain: `Each month you pay SARS the tax taken off staff pay, plus UIF and the skills levy (SDL). This is for ${monthYear(period)}.`,
        ruleDueOn: lastBusinessDayOnOrBefore(iso(Number(next.slice(0, 4)), Number(next.slice(5, 7)), 7)),
        yearEnd: end,
        optional: false,
        applies: hasEmployees,
      });
    }

    // VAT201: only once registered.
    for (const month of vatPeriodMonths(year?.vatCategory ?? "B")) {
      const periodYear = month >= Number(start.slice(5, 7)) ? Number(start.slice(0, 4)) : Number(end.slice(0, 4));
      const period = monthEnd(periodYear, month);
      add({
        id: `vat201-${period.slice(0, 7)}`,
        kind: "vat201",
        title: `VAT return for ${monthYear(period)} (VAT201)`,
        plain: `The VAT you charged less the VAT you paid, for the period ending ${monthYear(period)}. Filed and paid on eFiling by the last business day of the following month.`,
        ruleDueOn: lastBusinessDayOnOrBefore(endOfMonthAfter(period, 1)),
        yearEnd: end,
        optional: false,
        applies: vatRegistered,
      });
    }

    end = financialYearEnd(addDays(end, 1), profile.yearEndMonth);
  }
  // CIPC annual return: companies only, once a year from the anniversary of incorporation.
  if (!personal && input.incorporatedOn && /^\d{4}-\d{2}-\d{2}$/.test(input.incorporatedOn)) {
    const firstYear = Math.max(Number(input.incorporatedOn.slice(0, 4)) + 1, Number(profile.booksStart.slice(0, 4)));
    for (let year = firstYear; year <= Number(today.slice(0, 4)) + 1; year += 1) {
      const { anniversary, dueOn } = annualReturnDue(input.incorporatedOn, year);
      if (dueOn < profile.booksStart) continue;
      add({
        id: `cipc-ar-${year}`,
        kind: "cipc-ar",
        title: `CIPC annual return ${year}`,
        plain: `Once a year every company confirms its details with CIPC and pays a small fee based on turnover. It's due within ${ANNUAL_RETURN_BUSINESS_DAYS} business days of ${anniversary}, the anniversary of the company being registered. CIPC won't take it unless the beneficial ownership declaration is up to date, and it needs the financial accountability supplement (a few questions on how the books are kept).`,
        ruleDueOn: dueOn,
        yearEnd: financialYearEnd(anniversary, profile.yearEndMonth),
        optional: false,
        applies: true,
      });
    }
  }
  return out.sort((a, b) => a.dueOn.localeCompare(b.dueOn) || a.id.localeCompare(b.id));
};

export type ObligationStatus = "filed" | "overdue" | "today" | "soon" | "later";

/** Days from `today` to `dueOn` (negative once it has passed). */
export const daysBetween = (from: string, to: string): number => Math.round((utc(to).getTime() - utc(from).getTime()) / 86_400_000);

export const obligationStatus = (o: Pick<Obligation, "dueOn" | "filedOn">, today: string): ObligationStatus => {
  if (o.filedOn) return "filed";
  const days = daysBetween(today, o.dueOn);
  return days < 0 ? "overdue" : days === 0 ? "today" : days <= 14 ? "soon" : "later";
};

/**
 * The open obligations worth raising under "Needs attention", oldest first.
 * Older years never ticked off are the filings list's business, so only the
 * newest overdue one of each kind is kept; a finished year's provisional
 * instalments are over (the return settles them) and a missed optional
 * top-up needs no alarm. The Overview and the Tax page both read this, so a
 * payment is never listed twice.
 */
export const obligationsToRaise = (obligations: readonly Obligation[], today: string): Obligation[] => {
  const open = obligations
    .filter((o) => o.applies && o.filedOn === null)
    .filter((o) => !((o.kind === "irp6-1" || o.kind === "irp6-2") && o.yearEnd < today))
    .filter((o) => !(o.optional && o.dueOn < today))
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn) || a.id.localeCompare(b.id));
  const newestOverdue = new Map<ObligationKind, string>();
  for (const o of open) if (o.dueOn < today) newestOverdue.set(o.kind, o.id);
  return open.filter((o) => o.dueOn >= today || newestOverdue.get(o.kind) === o.id);
};

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/**
 * Why a provisional payment falls earlier than its calendar date, e.g.
 * "28 Feb is a Sunday". Null when the date stands (or the date was set by hand).
 */
export const provisionalDueNote = (o: Pick<Obligation, "kind" | "yearEnd" | "ruleDueOn" | "dueOn">): string | null => {
  if (o.dueOn !== o.ruleDueOn) return null;
  const month = Number(o.yearEnd.slice(5, 7));
  const year = Number(o.yearEnd.slice(0, 4));
  const natural =
    o.kind === "irp6-1"
      ? endOfMonthAfter(financialYearStart(o.yearEnd, month), 5)
      : o.kind === "irp6-2"
        ? o.yearEnd
        : o.kind === "irp6-3"
          ? month === 2
            ? monthEnd(year, 9)
            : endOfMonthAfter(o.yearEnd, 6)
          : null;
  if (!natural || natural === o.ruleDueOn) return null;
  const weekday = utc(natural).getUTCDay();
  const label = `${Number(natural.slice(8, 10))} ${SHORT_MONTHS[Number(natural.slice(5, 7)) - 1]}`;
  return weekday === 0 || weekday === 6 ? `${label} is a ${WEEKDAYS[weekday]}` : `${label} is a public holiday`;
};

/** The reminder id the server uses for an obligation of one set of books. */
export const reminderId = (profileId: string, obligationId: string): string => `tax-${profileId}-${obligationId}`.slice(0, 120);

/**
 * A suggested provisional payment, in cents. `taxForYearCents` is the year's
 * tax: the projection while the year is running, the real figure once it has
 * ended. It is a guide only: SARS charges penalties and interest for
 * underestimating, so the person decides the amount.
 *  - first: about half of the year's tax, less tax already taken off pay;
 *  - second: all that is still unpaid for the year;
 *  - top-up: whatever the finished year still owes.
 */
export const provisionalSuggestion = (
  kind: Extract<ObligationKind, "irp6-1" | "irp6-2" | "irp6-3">,
  input: { taxForYearCents: number; payeCents: number; provisional: TaxYearInputs["provisional"] },
): number => {
  const { taxForYearCents, payeCents, provisional } = input;
  const owing = Math.max(0, taxForYearCents - payeCents);
  if (kind === "irp6-1") return Math.round(owing / 2);
  if (kind === "irp6-2") return Math.max(0, owing - provisional.firstCents);
  return Math.max(0, owing - provisional.firstCents - provisional.secondCents - provisional.topUpCents);
};

/** Income over any 12 months above this makes VAT registration compulsory (raised to R2.3m from 1 April 2026; check SARS each budget). */
export const VAT_THRESHOLD_CENTS = 230_000_000;

export interface VatWatch {
  level: "ok" | "watch" | "register";
  /** Income over the last 12 months, as a share of the threshold. */
  share: number;
  incomeCents: number;
  thresholdCents: number;
  /** Plain words for the screen. */
  message: string;
}

/** Warns well before the compulsory VAT limit is reached (at 80%). */
export const vatThresholdWatch = (trailing12MonthIncomeCents: number): VatWatch => {
  const incomeCents = Math.max(0, trailing12MonthIncomeCents);
  const share = incomeCents / VAT_THRESHOLD_CENTS;
  const level = share >= 1 ? "register" : share >= 0.8 ? "watch" : "ok";
  const message =
    level === "register"
      ? "Income over the last 12 months is over the VAT limit. Registering is compulsory: SARS expects it within 21 days of the end of the month you crossed it. Speak to SARS or your tax practitioner now."
      : level === "watch"
        ? "Income over the last 12 months is close to the VAT limit. Plan for registering: once you pass it you must charge VAT."
        : "Income over the last 12 months is well under the VAT limit, so there's no need to register.";
  return { level, share, incomeCents, thresholdCents: VAT_THRESHOLD_CENTS, message };
};
